import React, { useState, useEffect } from "react";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { USStates } from "../../datasource";
import { Counties } from "../../datasource";
import {
  DropDownListComponent,
  MultiSelectComponent,
} from "@syncfusion/ej2-react-dropdowns";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  TextBoxComponent,
  TextAreaComponent,
} from "@syncfusion/ej2-react-inputs";
import { MAX } from "uuid";
import countiesData from "../../../counties_list.json";
import { getAuth } from "firebase/auth";
import ConfirmationModal from "../../../components/ConfirmationModal";

const DisasterDetail = ({ propId }) => {
  const disasterId = propId;
  const [disasterData, setDisasterData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempData, setTempData] = useState({});
  const [lastEdited, setLastEdited] = useState(null);
  const [availableCounties, setAvailableCounties] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [userData, setUserData] = useState("");
  const [canDeclareDisaster, setCanDeclareDisaster] = useState(false);
  const [canApproveDisaster, setCanApproveDisaster] = useState(false);
  const [userName, setUserName] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const selectedUserId = sessionStorage.getItem("userId");
  const employeeDetails = JSON.parse(sessionStorage.getItem("employeeDetails"));

  const fetchEmployeeDetails = async (centerId, employeeId) => {
    try {
      const employeeDocRef = doc(
        db,
        "centers",
        centerId,
        "employees",
        employeeId
      );
      const employeeDoc = await getDoc(employeeDocRef);

      if (!employeeDoc.exists()) {
        console.warn("Employee document not found");
        return null;
      }

      return employeeDoc.data();
    } catch (error) {
      console.error("Error fetching employee details:", error);
      return null;
    }
  };

  const updateUserState = (userData, employeeData) => {
    setUserRole(userData.user_role);
    setUserData(userData);
    setCanDeclareDisaster(employeeData?.can_declare_disaster || false);
    setCanApproveDisaster(employeeData?.can_approve_disaster || false);
    setUserName(`${employeeData?.first_name} ${employeeData?.last_name}`);
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));

        const userData = userDoc.data();
        const employeeId = employeeDetails?.id || selectedUserId;

        const employeeData = await fetchEmployeeDetails(
          userData.center_id,
          employeeId
        );
        console.log("employeeData", employeeData);

        updateUserState(userData, employeeData);
      }
    };
    fetchUserRole();
  }, []);

  const handleApprove = async () => {
    setShowConfirmModal(false);
    try {
      const id = Array.isArray(propId) ? propId[0] : propId;
      if (!id) return;

      const docRef = doc(db, "declared-disaster", id);
      const currentDate = new Date();

      await updateDoc(docRef, {
        disaster_status: "approved",
        approved_by: userName,
        approved_date: currentDate,
      });

      setTempData((prevData) => ({
        ...prevData,
        disaster_status: "approved",
        approved_by: userName,
        approved_date: currentDate,
      }));
      setDisasterData((prevData) => ({
        ...prevData,
        disaster_status: "approved",
        approved_by: userName,
        approved_date: currentDate,
      }));

      console.log("Disaster approved successfully.");
    } catch (error) {
      console.error("Error approving disaster:", error);
    }
  };

  const handleActivate = async () => {
    setShowActivateModal(false);
    try {
      const id = Array.isArray(propId) ? propId[0] : propId;
      if (!id) return;

      const docRef = doc(db, "declared-disaster", id);
      const currentDate = new Date();

      await updateDoc(docRef, {
        disaster_status: "active",
        activated_by: userName,
        activated_date: currentDate,
      });

      setTempData((prevData) => ({
        ...prevData,
        disaster_status: "active",
        activated_by: userName,
        activated_date: currentDate,
      }));
      setDisasterData((prevData) => ({
        ...prevData,
        disaster_status: "active",
        activated_by: userName,
        activated_date: currentDate,
      }));

      console.log("Disaster activated successfully.");
    } catch (error) {
      console.error("Error activating disaster:", error);
    }
  };

  const handleCloseActivateModal = async () => {
    setShowActivateModal(false);
    try {
      const id = Array.isArray(propId) ? propId[0] : propId;
      if (!id) return;

      const docRef = doc(db, "declared-disaster", id);
      const currentDate = new Date();

      await updateDoc(docRef, {
        disaster_status: "active",
        active_by: userName,
        active_date: currentDate,
      });

      setTempData((prevData) => ({
        ...prevData,
        disaster_status: "active",
        active_by: userName,
        active_date: currentDate,
      }));
      setDisasterData((prevData) => ({
        ...prevData,
        disaster_status: "active",
        active_by: userName,
        active_date: currentDate,
      }));

      console.log("Disaster activated successfully.");
    } catch (error) {
      console.error("Error activating disaster:", error);
    }
  };

  useEffect(() => {
    const fetchDisaster = async () => {
      const id = Array.isArray(disasterId) ? disasterId[0] : disasterId;

      if (!id) {
        console.error("No valid ID provided to DisasterDetail component.");
        return;
      }

      try {
        const docRef = doc(db, "declared-disaster", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setDisasterData({ id, ...docSnap.data() });
          const data = docSnap.data();
          if (typeof data.disaster_type === "string") {
            data.disaster_type = [data.disaster_type];
          }
          setTempData({ id, ...data });
          setLastEdited(new Date().toLocaleString());
        }
      } catch (error) {
        console.error("Error fetching disaster data:", error);
      }
    };

    fetchDisaster();
  }, [disasterId]);

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setIsEditMode(false);
    setTempData(disasterData);
  };

  const handleSaveClick = async () => {
    try {
      const id = Array.isArray(propId) ? propId[0] : propId;

      if (!id || typeof id !== "string") {
        console.error("Invalid document ID:", id);
        return;
      }

      const docRef = doc(db, "declared-disaster", id);
      await updateDoc(docRef, tempData);
      setDisasterData(tempData);
      setIsEditMode(false);
      setLastEdited(new Date().toLocaleString());
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  // Update available counties when disaster_state changes
  useEffect(() => {
    if (tempData.disaster_state) {
      updateAvailableCounties(tempData.disaster_state);
    }
  }, [tempData.disaster_state]);

  // Update counties based on selected states
  const updateAvailableCounties = (selectedStates) => {
    if (!selectedStates || selectedStates.length === 0) {
      setAvailableCounties([]);
    } else {
      const filteredCounties = countiesData.filter((county) =>
        selectedStates.includes(county.State)
      );
      setAvailableCounties(filteredCounties.map((county) => county.County));
    }
  };

  const handleInputChange = (name, value) => {
    setTempData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const formatFirebaseDate = (firebaseDate) => {
    if (!firebaseDate) return null;

    if (firebaseDate.seconds) {
      return new Date(firebaseDate.seconds * 1000); // Convert Timestamp to Date
    }

    return new Date(firebaseDate); // If it's already a Date object
  };

  return (
    <div className="w-full px-4">
      <header className="flex items-center justify-between gap-2">
        <p className="text-left text-sm font-bold text-blue-600">DETAILS</p>

        <div className="mr-3 flex items-center gap-2">
          {isEditMode ? (
            <>
              <button
                type="button"
                className="btn-default flex cursor-pointer items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white"
                onClick={handleSaveClick}
              >
                <FaSave className="h-3 w-3" /> Save
              </button>
              <button
                type="button"
                className="btn-cancel flex cursor-pointer items-center gap-1 rounded bg-gray-400 px-3 py-1.5 text-xs font-semibold text-black"
                onClick={handleCancelClick}
              >
                <FaTimes className="h-3 w-3" /> Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-default flex cursor-pointer items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white"
              onClick={handleEditClick}
            >
              <FaEdit className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4">
        <div className="flex w-full gap-4">
          {/* Name */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            {/* Name Field */}
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Name
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <TextBoxComponent
                name="disaster_name"
                value={tempData.disaster_name || ""}
                change={(e) => handleInputChange("disaster_name", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
        </div>
        <div className="flex w-full gap-4">
          {/* Description */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Description
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex self-stretch">
              <TextAreaComponent
                name="description"
                value={tempData.description || ""}
                change={(e) => handleInputChange("description", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  margin: "0",
                  padding: "5px 15px",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  height: "5em",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
        </div>
        <div className="flex w-full gap-4">
          {/* Type of Disaster Field */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Type of Disaster
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <MultiSelectComponent
                name="disaster_type"
                dataSource={[
                  "Fire",
                  "Flood",
                  "Tornado",
                  "Hurricane",
                  "Earthquake",
                  "Volcano",
                ]}
                value={
                  Array.isArray(tempData.disaster_type)
                    ? tempData.disaster_type
                    : []
                }
                change={(e) => handleInputChange("disaster_type", e.value)}
                enabled={isEditMode}
                showSelectAll={true}
                mode="Box"
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Disaster Imapct Level */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Disaster impact level
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DropDownListComponent
                name="disaster_impact"
                dataSource={["Minor", "Moderate", "Major", "Catastrophic"]}
                value={tempData.disaster_impact || ""}
                change={(e) => handleInputChange("disaster_impact", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
        </div>
        <div className="flex w-full gap-4">
          {/* Stage of Disaster Field */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Stage of Disaster
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DropDownListComponent
                name="disaster_stage"
                dataSource={["Response", "Recovery", "Relief"]}
                value={tempData.disaster_stage || ""}
                change={(e) => handleInputChange("disaster_stage", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Declaration Level */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Disaster declaration level
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <MultiSelectComponent
                dataSource={["Local", "State", "Federal"]}
                name="disaster_level"
                value={tempData.disaster_level || ""}
                change={(e) => handleInputChange("disaster_level", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Affected States */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Affected States
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <MultiSelectComponent
                name="disaster_state"
                dataSource={[
                  ...new Set(countiesData.map((item) => item.State)),
                ]}
                value={
                  Array.isArray(tempData.disaster_state)
                    ? tempData.disaster_state
                    : []
                }
                change={(e) => handleInputChange("disaster_state", e.value)}
                showSelectAll={true}
                allowFiltering={true}
                enabled={false}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Affected Counties */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Affected Counties
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <MultiSelectComponent
                name="affected_counties"
                dataSource={[
                  ...new Set(countiesData.map((item) => item.County)),
                ]}
                value={
                  Array.isArray(tempData.affected_counties)
                    ? tempData.affected_counties
                    : []
                }
                change={(e) => handleInputChange("affected_counties", e.value)}
                showSelectAll={true}
                allowFiltering={true}
                enabled={false}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex w-full gap-4">
          {/* Declared by */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Declared By
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <TextBoxComponent
                name="declared_by"
                value={tempData.declared_by || ""}
                change={(e) => handleInputChange("declared_by", e.value)}
                enabled={false}
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Declared date */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Declared Date
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DatePickerComponent
                name="date_declared"
                value={formatFirebaseDate(tempData.date_declared || "")}
                change={(e) => handleInputChange("date_declared", e.value)}
                enabled={false}
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Approved by */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Approved By
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <TextBoxComponent
                name="approved_by"
                value={tempData.approved_by || ""}
                change={(e) => handleInputChange("approved_by", e.value)}
                enabled={false}
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Approved date */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Approved Date
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DatePickerComponent
                name="approved_date"
                value={formatFirebaseDate(tempData.approved_date || "")}
                change={(e) => handleInputChange("approved_date", e.value)}
                enabled={false}
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
        </div>
        <div className="flex w-full gap-4">
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Date of the Disaster
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DatePickerComponent
                name="disaster_date"
                value={formatFirebaseDate(tempData.disaster_date || "")}
                change={(e) => handleInputChange("disaster_date", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
              />
            </div>
          </div>
          {/* Status Field */}
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <div className="text-gray-600 relative text-left text-xs leading-[20px]">
              Status
              <p className="absolute right-[-15px] top-[4px] text-[26px] text-red-500">
                *
              </p>
            </div>
            <div className="box-border flex h-10 flex-row items-center justify-between self-stretch">
              <DropDownListComponent
                name="disaster_status"
                dataSource={
                  tempData.disaster_status === "declared" ||
                  tempData.disaster_status === "approved"
                    ? ["declared", "approved", "active"]
                    : ["active", "inactive", "closed"]
                }
                value={tempData.disaster_status || ""}
                change={(e) => handleInputChange("disaster_status", e.value)}
                enabled={isEditMode}
                style={{
                  backgroundColor: isEditMode ? "" : "#f3f4f6",
                  color: "#000000",
                  fontSize: "14px",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
                cssClass="e-outline w-full"
                // enabled={
                //   isEditMode &&
                //   !(
                //     tempData.disaster_status === "declared" ||
                //     tempData.disaster_status === "approved"
                //   )
                // }
              />

              {tempData.disaster_status === "declared" &&
                canApproveDisaster && (
                  <button
                    type="button"
                    className="btn-default flex cursor-pointer items-center gap-1 rounded px-3 py-1.5 text-xs font-semibold text-white"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    <FaSave className="h-3 w-3" /> Approve
                  </button>
                )}

              {tempData.disaster_status === "approved" &&
                canApproveDisaster && (
                  <button
                    type="button"
                    className="btn-default flex cursor-pointer items-center gap-1 rounded px-3 py-1.5 text-xs font-semibold text-white"
                    onClick={() => setShowActivateModal(true)}
                  >
                    <FaSave className="h-3 w-3" /> Activate
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleApprove}
        title="Confirm Approve Disaster"
        message="Are you sure you want to approve this disaster?"
      />
      <ConfirmationModal
        isOpen={showActivateModal}
        onClose={handleCloseActivateModal}
        onConfirm={handleActivate}
        title="Confirm Activate Disaster"
        message="Are you sure you want to activate this disaster?"
      />
    </div>
  );
};

export default DisasterDetail;
